import * as MixpanelProfilePageActions from '@analytics/mixpanel/actions/ProfilePage';
import * as CelebritiesAPI from '@api/Celebrities';
import Routes from '@constants/routes';
import HowWorksSection from '@features/common/HowWorksSection/HowWorksSection';
import ProfileForm from '@features/profile/ProfileForm';
import useWindowWidth from '@hooks/useWindowWidth';
import BaseLayout from '@layouts/Base/BaseLayout';
import { useRouter } from 'next/router';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import {
  BackgroundContainer,
  BackgroundGradientSmall,
  BiggerScreenBackgroundContainerShade,
  BiggerScreenPlayButtonArea,
  BiggerScreenVideo,
  BiggerScreenVideoContainer,
  FullscreenVideo,
  PlayProfileIcon,
  PlayProfileLargeIcon,
  ProfileFormContainer,
} from './Profile.styles';
import { ProfileProps } from './Profile.types';

const Profile = ({ ssrCelebInfo, ssrLocale, user }: ProfileProps): JSX.Element => {
  const [celebrityInfo, setCelebrityInfo] = useState<CelebritiesAPI.CelebrityResponse>(ssrCelebInfo);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showBiggerScreenVideo, setShowBiggerScreenVideo] = useState<boolean>(false);
  const [isBiggerScreenVideoPlaying, setIsBiggerScreenVideoPlaying] = useState<boolean>(false);
  const [isBiggerScreenVideoLoaded, setIsBiggerScreenVideoLoaded] = useState<boolean>(false);
  const [activeLanguage, setActivelanguage] = useState<string>(ssrLocale);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const windowWidth = useWindowWidth();
  const videoRef = useRef() as MutableRefObject<
    HTMLVideoElement & {
      webkitEnterFullscreen(): () => void;
      mozRequestFullScreen(): () => void;
      msRequestFullscreen(): () => void;
    }
  >;
  const videoRef2 = useRef() as MutableRefObject<HTMLVideoElement>;
  const router = useRouter();

  useEffect(() => {
    MixpanelProfilePageActions.onProfilePageLoaded({ router, celebrityDetails: { celebrityName: celebrityInfo.slug } });
    setIsPageLoaded(true);
  }, []);

  // Fullscreen Video Handling
  useEffect(() => {
    const element = videoRef.current;
    const updateFullscreen = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    element.addEventListener('fullscreenchange', updateFullscreen);
    return () => element.removeEventListener('fullscreenchange', updateFullscreen);
  }, []);

  useEffect(() => {
    if (!isFullscreen) videoRef.current?.pause();
  }, [isFullscreen]);
  ///////////////////////////////////

  // Bigger Screens Video Handling
  useEffect(() => {
    const isLgXl = windowWidth > 1023;
    if (!isLgXl && showBiggerScreenVideo) {
      videoRef2.current?.pause();
      setIsBiggerScreenVideoPlaying(false);
      setShowBiggerScreenVideo(false);
    }
  }, [windowWidth]);
  ////////////////////////////////

  useEffect(() => {
    const getCelebrityInfo = async () => {
      if (celebrityInfo.slug == '') return;
      const response = await CelebritiesAPI.GetCelebrityInfo({ slug: celebrityInfo.slug });
      if (!response.success && response.code == 'not-found') return router.push(Routes.ERROR_404);
      const celebInfo = response.success && response.data?.slug ? (response.data as CelebritiesAPI.CelebrityResponse) : celebrityInfo;
      if (!celebInfo.available) {
        await router.push(Routes.HOME);
        return;
      }
      setCelebrityInfo(celebInfo);
    };

    if (router.locale == activeLanguage) return;
    getCelebrityInfo();
    setActivelanguage(router.locale + '');
  }, [router]);

  const openFullscreenAndPlay = async () => {
    MixpanelProfilePageActions.onProfileVideoPlayed({ router, celebrityDetails: { celebrityName: celebrityInfo.slug } });
    videoRef.current.currentTime = 0;
    videoRef.current.requestFullscreen
      ? await videoRef.current.requestFullscreen()
      : videoRef.current.webkitEnterFullscreen
      ? await videoRef.current.webkitEnterFullscreen()
      : videoRef.current.mozRequestFullScreen
      ? await videoRef.current.mozRequestFullScreen()
      : videoRef.current.msRequestFullscreen
      ? await videoRef.current.msRequestFullscreen()
      : undefined;
    videoRef.current?.play().catch(e => console.log(e));
  };

  const fullscreenCloseVideo = () => {
    const document: any = window.document;
    document.exitFullscreen
      ? document.exitFullscreen()
      : document.webkitExitFullscreen
      ? document.webkitExitFullscreen()
      : document.mozCancelFullScreen
      ? document.mozCancelFullScreen()
      : document.msExitFullscreen
      ? document.msExitFullscreen()
      : undefined;
  };

  const biggerScreenOpenVideo = () => {
    MixpanelProfilePageActions.onProfileVideoPlayed({ router, celebrityDetails: { celebrityName: celebrityInfo.slug } });
    videoRef2.current.currentTime = 0;
    setShowBiggerScreenVideo(true);
    videoRef2.current?.play().catch(e => console.log(e));
    setIsBiggerScreenVideoPlaying(true);
  };

  const biggerScreenCloseVideo = () => {
    setShowBiggerScreenVideo(false);
  };

  const biggerScreenSetVideoLoaded = () => {
    setIsBiggerScreenVideoLoaded(true);
  };

  const biggerScreenPlayPauseVideo = () => {
    if (!isBiggerScreenVideoLoaded) return;
    if (videoRef2.current?.paused) {
      videoRef2.current?.play().catch(e => console.log(e));
      setIsBiggerScreenVideoPlaying(true);
    } else {
      videoRef2.current?.pause();
      setIsBiggerScreenVideoPlaying(false);
    }
  };

  const Background = (
    <>
      <BackgroundContainer image={celebrityInfo.photo}>
        <BiggerScreenBackgroundContainerShade isBiggerScreenVideoPlaying={showBiggerScreenVideo} />
      </BackgroundContainer>
      {celebrityInfo?.video && (
        <>
          <BackgroundGradientSmall onClick={openFullscreenAndPlay}>
            <PlayProfileIcon />
          </BackgroundGradientSmall>
        </>
      )}
      <FullscreenVideo show={isFullscreen} controls src={celebrityInfo?.video} ref={videoRef} onEnded={fullscreenCloseVideo} />
    </>
  );

  return (
    <BaseLayout
      showFooter={true}
      headerProps={{ userName: user?.name }}
      background={Background}
      infoOG={{
        ssrLocale,
        image: celebrityInfo.photo,
        title:
          ssrLocale === 'ar'
            ? `رسائل شخصية وفيديوهات من ${celebrityInfo.fullname} | Yela`
            : `Personalized messages & videos from ${celebrityInfo.fullname} | Yela`,
        description:
          ssrLocale === 'ar'
            ? `احصل على رسائل شخصية، فيديو وصوتية، من ${celebrityInfo.fullname} لك ولأصدقائك. تواصل مع أهم المشاهير الآن على Yela!`
            : `Get a personalised video or voice message from ${celebrityInfo.fullname} for you or your friends. Connect with A-list celebrities now on Yela!`,
      }}
    >
      <ProfileFormContainer>
        <ProfileForm celebrityInfo={celebrityInfo} user={user} />
      </ProfileFormContainer>
      {!showBiggerScreenVideo && celebrityInfo?.video && (
        <BiggerScreenPlayButtonArea>
          <PlayProfileLargeIcon onClick={biggerScreenOpenVideo} />
        </BiggerScreenPlayButtonArea>
      )}
      <BiggerScreenVideoContainer show={showBiggerScreenVideo} videoLoaded={isBiggerScreenVideoLoaded}>
        {isPageLoaded && (
          <BiggerScreenVideo
            src={celebrityInfo?.video}
            ref={videoRef2}
            onClick={biggerScreenPlayPauseVideo}
            onEnded={biggerScreenCloseVideo}
            onLoadedData={biggerScreenSetVideoLoaded}
          />
        )}
        {showBiggerScreenVideo && !isBiggerScreenVideoPlaying && <PlayProfileLargeIcon onClick={biggerScreenPlayPauseVideo} />}
      </BiggerScreenVideoContainer>
      <HowWorksSection isProfile />
    </BaseLayout>
  );
};

export default Profile;
